import React, { useState, useEffect, useCallback } from 'react';
import { FaWifi, FaExclamationTriangle, FaTimes } from 'react-icons/fa';
import WebSocketManager from '../../utils/WebSocketManager';
import './ConnectionStatus.css';

const ConnectionStatus = () => {
  const [connectionStatus, setConnectionStatus] = useState({
    isConnected: true,
    isHealthy: true,
    showNotification: false,
    showSuccessNotification: false,
    consecutiveErrors: 0,
    lastError: null,
    isInitializing: true // Add initialization flag
  });

  const [isDismissed, setIsDismissed] = useState(false);
  const [initialCheckDone, setInitialCheckDone] = useState(false);
  const wsManager = WebSocketManager.getInstance();

  const checkConnectionHealth = useCallback(() => {
    try {
      const health = wsManager.getConnectionHealth();
      const isConnected = wsManager.connection && wsManager.connection.readyState === WebSocket.OPEN;
      
      const newStatus = {
        isConnected,
        isHealthy: health.isHealthy,
        consecutiveErrors: health.consecutiveErrors,
        lastError: health.lastError || null,
        isInitializing: false
      };

      // Only show notification after initial check is done and if there are real issues
      // Don't show during the first few seconds after page load
      const shouldShowNotification = initialCheckDone && 
        (!isConnected || !health.isHealthy || health.consecutiveErrors > 2) && 
        !isDismissed;
      
      setConnectionStatus(prev => {
        const newState = {
          ...prev,
          ...newStatus,
          showNotification: shouldShowNotification
        };

        // Auto-hide notification when connection is restored
        if (isConnected && health.isHealthy && health.consecutiveErrors === 0) {
          setIsDismissed(false);
          // Show success notification briefly when connection is restored ONLY if there was a previous error
          if (prev.showNotification || prev.consecutiveErrors > 0) {
            setTimeout(() => {
              setConnectionStatus(current => ({
                ...current,
                showSuccessNotification: false
              }));
            }, 3000);
            
            return {
              ...newState,
              showNotification: false,
              showSuccessNotification: true
            };
          }
        }

        return newState;
      });

    } catch (error) {
      console.error('Error checking connection health:', error);
      // Only set error state if we're past the initialization period
      if (initialCheckDone) {
        setConnectionStatus(prev => ({
          ...prev,
          isConnected: false,
          isHealthy: false,
          showNotification: !isDismissed,
          lastError: error.message,
          isInitializing: false
        }));
      }
    }
  }, [wsManager, initialCheckDone, isDismissed]);

  useEffect(() => {
    let healthCheckInterval;
    let connectionCheckInterval;
    let initializationTimeout;
    let connectionStateCleanup;

    // Listen to WebSocket connection state changes
    connectionStateCleanup = wsManager.addConnectionStateListener((state) => {
      if (state.type === 'connected') {
        setConnectionStatus(prev => {
          // Only show success notification if we were previously disconnected or had errors
          const shouldShowSuccess = prev.showNotification || prev.consecutiveErrors > 0 || !prev.isConnected;
          
          const newState = {
            ...prev,
            isConnected: true,
            isHealthy: true,
            showNotification: false,
            showSuccessNotification: shouldShowSuccess && initialCheckDone, // Only after initial check
            consecutiveErrors: 0,
            lastError: null
          };
          
          // Hide success notification after 3 seconds if shown
          if (shouldShowSuccess && initialCheckDone) {
            setTimeout(() => {
              setConnectionStatus(current => ({
                ...current,
                showSuccessNotification: false
              }));
            }, 3000);
          }
          
          return newState;
        });
        setIsDismissed(false);
        
      } else if ((state.type === 'disconnected' || state.type === 'error') && initialCheckDone) {
        checkConnectionHealth();
      }
    });

    // Wait for initial WebSocket connection attempt
    initializationTimeout = setTimeout(() => {
      setInitialCheckDone(true);
      // Do first real check after giving WebSocket time to connect
      checkConnectionHealth();
    }, 8000); // Wait 8 seconds before starting to show error notifications

    // Set up intervals
    healthCheckInterval = setInterval(() => {
      if (initialCheckDone) {
        checkConnectionHealth();
      }
    }, 2000); // Check every 2 seconds
    
    // Also check when connection state changes
    connectionCheckInterval = setInterval(() => {
      if (initialCheckDone) {
        const isConnected = wsManager.connection && wsManager.connection.readyState === WebSocket.OPEN;
        if (!isConnected) {
          checkConnectionHealth();
        }
      }
    }, 1000);

    return () => {
      if (healthCheckInterval) clearInterval(healthCheckInterval);
      if (connectionCheckInterval) clearInterval(connectionCheckInterval);
      if (initializationTimeout) clearTimeout(initializationTimeout);
      if (connectionStateCleanup) connectionStateCleanup();
    };
  }, [wsManager, initialCheckDone, checkConnectionHealth]);

  const handleDismiss = () => {
    setIsDismissed(true);
    setConnectionStatus(prev => ({
      ...prev,
      showNotification: false
    }));
  };

  const handleRetry = async () => {
    try {
      await wsManager.ensureConnection();
      setIsDismissed(false);
    } catch (error) {
      console.error('Retry connection failed:', error);
    }
  };

  if (!connectionStatus.showNotification && !connectionStatus.showSuccessNotification) {
    return null;
  }

  const getStatusMessage = () => {
    if (connectionStatus.showSuccessNotification) {
      return {
        title: 'Connected',
        message: 'Successfully connected to STEEM network',
        type: 'success'
      };
    }
    
    if (!connectionStatus.isConnected) {
      return {
        title: 'Connection Lost',
        message: 'Unable to connect to the STEEM network. Some features may not work properly.',
        type: 'error'
      };
    }
    
    if (!connectionStatus.isHealthy) {
      return {
        title: 'Connection Issues',
        message: `Network connection is unstable. ${connectionStatus.consecutiveErrors} consecutive errors detected.`,
        type: 'warning'
      };
    }
    
    return {
      title: 'Network Issues',
      message: 'Experiencing connectivity problems. Retrying...',
      type: 'warning'
    };
  };

  const status = getStatusMessage();
  const isSuccessNotification = connectionStatus.showSuccessNotification;

  return (
    <div className={`connection-status-notification ${status.type}`}>
      <div className="connection-status-content">
        <div className="connection-status-icon">
          {status.type === 'success' ? (
            <FaWifi className="wifi-success" />
          ) : status.type === 'error' ? (
            <FaExclamationTriangle />
          ) : (
            <FaWifi className="wifi-warning" />
          )}
        </div>
        
        <div className="connection-status-message">
          <h4 className="connection-status-title">{status.title}</h4>
          <p className="connection-status-description">{status.message}</p>
          {connectionStatus.lastError && !isSuccessNotification && (
            <p className="connection-status-error">{connectionStatus.lastError}</p>
          )}
        </div>
        
        {!isSuccessNotification && (
          <div className="connection-status-actions">
            <button 
              className="connection-retry-btn"
              onClick={handleRetry}
              title="Retry Connection"
            >
              Retry
            </button>
            <button 
              className="connection-dismiss-btn"
              onClick={handleDismiss}
              title="Dismiss"
            >
              <FaTimes />
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default ConnectionStatus;
